import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getHomepageConfig, getProductById } from '../../services/homepage.service';
import { collection, query, where, documentId, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Link } from 'react-router-dom';
import ProductCard from '../ui/ProductCard';

const FeaturedSection = () => {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Hooks must be unconditional
    const scrollContainerRef = React.useRef(null);
    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 300;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const loadFeatured = async () => {
            try {
                // ... logic
                const config = await getHomepageConfig();
                if (!config || !config.featuredProducts || config.featuredProducts.length === 0) {
                    setLoading(false);
                    return;
                }
                // ... (rest of loading logic same as before)
                const featuredItems = config.featuredProducts.filter(item => {
                    if (typeof item === 'string') return true;
                    return item.isActive !== false;
                });

                if (featuredItems.length === 0) {
                    setLoading(false);
                    return;
                }

                const ids = featuredItems.map(item => typeof item === 'string' ? item : item.id);
                let fetchedDocs = [];

                if (ids.length <= 10) {
                    const q = query(collection(db, "products"), where(documentId(), "in", ids));
                    const snap = await getDocs(q);
                    fetchedDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                } else {
                    const promises = ids.map(id => getProductById(id));
                    const results = await Promise.all(promises);
                    fetchedDocs = results.filter(Boolean);
                }

                const ordered = featuredItems.map(item => {
                    const id = typeof item === 'string' ? item : item.id;
                    const p = fetchedDocs.find(fd => fd.id === id);
                    if (!p) return null;
                    return p;
                }).filter(Boolean);

                setProducts(ordered);

            } catch (err) {
                console.error("Failed to load featured products", err);
            } finally {
                setLoading(false);
            }
        };

        loadFeatured();
    }, []);

    // Check loading/empty AFTER hooks
    if (loading) return null;
    if (products.length === 0) return null;

    return (
        <section className="py-20 bg-transparent group/section">
            <div className="container mx-auto px-4 max-w-[1200px]">
                <div className="flex flex-col md:flex-row items-end justify-between mb-10 gap-4">
                    {/* ... Header ... */}
                    <div>
                        <h2 className="text-4xl text-fecos-dark font-serif font-bold text-gray-900 mb-2">
                            {t('home.featured.title', 'Öne Çıkanlar')}
                        </h2>
                        <p className="text-gray-500 text-lg">
                            {t('home.featured.subtitle', 'En çok tercih edilen ürünler')}
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Arrows */}
                        <div className="hidden md:flex items-center gap-2">
                            <button
                                onClick={() => scroll('left')}
                                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-fecos-accent hover:text-white hover:border-transparent transition-all"
                            >
                                <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <button
                                onClick={() => scroll('right')}
                                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-fecos-accent hover:text-white hover:border-transparent transition-all"
                            >
                                <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5l7 7-7 7" /></svg>
                            </button>
                        </div>

                        <Link to="/all-products" className="text-fecos-accent font-bold hover:underline flex items-center gap-1">
                            {t('home.featured.shopAll', 'Tümünü Gör')}
                        </Link>
                    </div>
                </div>

                <div className="relative">
                    <div
                        ref={scrollContainerRef}
                        className="flex overflow-x-auto gap-4 md:gap-6 pb-8 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {products.map(product => (
                            <div
                                key={product.id}
                                className="min-w-[260px] w-[75%] sm:w-[calc(50%-12px)] md:w-[calc(33.33%-16px)] lg:w-[calc(25%-18px)] snap-start flex-shrink-0"
                            >
                                <ProductCard product={product} />
                            </div>
                        ))}

                        {/* Spacer for right padding on mobile scroll */}
                        <div className="w-[1px] flex-shrink-0 h-1" />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturedSection;
